import { useApiQuery, useApiMutation, invalidateQueries } from './useApiQuery';
import apiClient from '../services/api/client';
import { User } from '../types';
import { NavItem } from '../constants/sidebar';
import { resolveActiveEstablishment } from '../utils/activeEstablishment';
import { InternalAxiosRequestConfig, AxiosError } from 'axios';

interface ConfigWithSkipAutoLogout extends InternalAxiosRequestConfig {
  skipAutoLogout?: boolean;
}

// Types for API responses
interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  access_token: string;
  user: {
    id: string;
    email: string;
    name: string;
    roles: string[];
    establishmentId?: string;
    active: boolean;
  };
}

interface MeResponse {
  id: string;
  name: string;
  email: string;
  establishments: Array<{
    id: string;
    name: string;
    roles: string[];
  }>;
  permissions: string[];
}

interface NavigationResponse {
  navigation: NavItem[];
}

// Query keys
export const authQueryKeys = {
  me: ['auth', 'me'] as const,
  navigation: ['auth', 'navigation'] as const,
};

// Login mutation
export const useLogin = () => {
  return useApiMutation<LoginResponse, LoginRequest>(
    async (credentials) => {
      // Backend expects 'login' field instead of 'email'
      const { data } = await apiClient.post<LoginResponse>('/auth/login', {
        login: credentials.email,
        password: credentials.password
      });
      return data;
    },
    {
      onSuccess: (data) => {
        try {
          // Store the token
          localStorage.setItem('mep_token', data.access_token);
          
          // Invalidate queries to refetch with new auth
          invalidateQueries('auth:');
        } catch (error) {
          console.error('Error in login mutation onSuccess:', error);
        }
      },
      onError: (error) => {
        console.error('Login mutation failed:', error);
      }
    }
  );
};

// Logout mutation
export const useLogout = () => {
  return useApiMutation<void, void>(
    async () => {
      const token = localStorage.getItem('mep_token');
      if (token) {
        await apiClient.post('/auth/logout');
      }
    },
    {
      onSuccess: () => {
        try {
          // Clear ALL auth data including establishment
          localStorage.removeItem('mep_token');
          localStorage.removeItem('mep_user');
          localStorage.removeItem('mep_current_establishment');

          // Clear all queries
          invalidateQueries();
        } catch (error) {
          console.error('Error in logout mutation:', error);
        }
      }
    }
  );
};

// Get current user query
export const useMe = (enabled: boolean = true) => {
  return useApiQuery<MeResponse>(
    authQueryKeys.me.join(':'),
    async () => {
      const { data } = await apiClient.get<MeResponse>('/auth/me');

      // Map backend user to expected format
      const establishment = resolveActiveEstablishment(
        data.establishments,
        localStorage.getItem('mep_current_establishment'),
      );
      const user: User = {
        id: data.id,
        name: data.name,
        email: data.email,
        roles: establishment?.roles ?? [],
        establishmentId: establishment?.id,
        establishment: establishment ? {
          id: establishment.id,
          name: establishment.name,
          document: '', // Not provided in UserResponse
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: undefined,
          updatedBy: undefined,
          deletedAt: null,
          deletedBy: undefined
        } : undefined,
        establishments: data.establishments,
        phone: null,
        unit: null,
        active: true, // Not provided in UserResponse, default to true
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Store mapped user data
      localStorage.setItem('mep_user', JSON.stringify(user));

      // Set establishment header for API calls
      // Validate that saved establishment is accessible by current user
      if (data.establishments && data.establishments.length > 0) {
        const savedEstablishmentId = localStorage.getItem('mep_current_establishment');

        if (!savedEstablishmentId) {
          // No saved establishment (initial load), use first available
          const firstEstablishment = data.establishments[0];
          if (firstEstablishment) {
            localStorage.setItem('mep_current_establishment', firstEstablishment.id);
            apiClient.defaults.headers.common['x-establishment-id'] = firstEstablishment.id;
            if (typeof window !== 'undefined' && window.apiClient) {
              window.apiClient.defaults.headers.common['x-establishment-id'] = firstEstablishment.id;
            }
          }
        } else {
          // Validate if user has access to saved establishment
          const hasAccess = data.establishments.some(e => e.id === savedEstablishmentId);

          if (hasAccess) {
            // User has access, use saved establishment
            apiClient.defaults.headers.common['x-establishment-id'] = savedEstablishmentId;
            if (typeof window !== 'undefined' && window.apiClient) {
              window.apiClient.defaults.headers.common['x-establishment-id'] = savedEstablishmentId;
            }
          } else {
            // User does NOT have access to saved establishment (likely different user logged in)
            // Clear and use first available establishment
            console.log('[useMe] Saved establishment not accessible by current user, using first available');
            const firstEstablishment = data.establishments[0];
            if (firstEstablishment) {
              localStorage.setItem('mep_current_establishment', firstEstablishment.id);
              apiClient.defaults.headers.common['x-establishment-id'] = firstEstablishment.id;
              if (typeof window !== 'undefined' && window.apiClient) {
                window.apiClient.defaults.headers.common['x-establishment-id'] = firstEstablishment.id;
              }
            }
          }
        }
      }

      return data;
    },
    {
      enabled,
      cacheTime: 5 * 60 * 1000, // 5 minutes
      onError: (error: unknown) => {
        if (error instanceof Error && 'response' in error) {
          const axiosError = error as AxiosError;
          if (axiosError.response?.status === 401) {
            console.log('[useMe] 401 error, user not authenticated');
          }
        }
      }
    }
  );
};

// Get navigation query
export const useNavigation = (enabled: boolean = true) => {
  // Couple navigation to current establishment to avoid serving stale menus
  const establishmentId = typeof window !== 'undefined'
    ? (localStorage.getItem('mep_current_establishment') || 'none')
    : 'none';

  return useApiQuery<NavigationResponse>(
    ['auth', 'navigation', establishmentId],
    async () => {
      const { data } = await apiClient.get<NavigationResponse>('/config/navigation');
      return data;
    },
    {
      enabled,
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: (error: unknown) => {
        if (error instanceof Error && 'response' in error) {
          const axiosError = error as AxiosError;
          if (axiosError.response?.status === 401) {
            console.log('[useNavigation] 401 error, user not authenticated');
          }
        }
      }
    }
  );
};

// Refresh token mutation
export const useRefreshToken = () => {
  return useApiMutation<{ access_token: string }, void>(
    async () => {
      const { data } = await apiClient.post<{ access_token: string }>('/auth/refresh', {}, {
        skipAutoLogout: true // Skip automatic logout on 401 for refresh token requests
      } as ConfigWithSkipAutoLogout);
      return data;
    },
    {
      onSuccess: (data) => {
        try {
          // Update stored token
          localStorage.setItem('mep_token', data.access_token);
        } catch (error) {
          console.error('Error in refresh token onSuccess:', error);
        }
      },
      onError: (error) => {
        console.error('Refresh token mutation failed:', error);
      }
    }
  );
};

// Check session expiry query
export const useCheckSessionExpiry = (enabled: boolean = true) => {
  return useApiQuery<{ willExpire: boolean; expiresAt: number }>(
    ['auth', 'session-status'].join(':'),
    async () => {
      try {
        const { data } = await apiClient.get<{ willExpire: boolean; expiresAt: number }>(
          '/auth/session-status',
        );
        return data;
      } catch (error: unknown) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError?.response?.status === 401) {
          // Session expired
          return { willExpire: true, expiresAt: 0 };
        }
        throw error;
      }
    },
    {
      enabled,
      cacheTime: 30 * 1000, // 30 seconds
      refetchInterval: enabled ? 30 * 1000 : undefined, // Check every 30 seconds
    }
  );
};

// Re-export types
export type { LoginRequest, LoginResponse, MeResponse, NavigationResponse };
